import { useEffect, useState } from "react";
import { Loader2, CalendarX2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PHRASE = "OPSCHONEN";
const DEFAULT_DATE = "2026-10-06";
const fmtDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

// Alles van vóór een datum wissen (niet-beheerders + hun data, systeemdata); betaalde accounts blijven staan.
export default function CleanupBeforeDialog({ open, onClose, onDone }) {
  const [before, setBefore] = useState(DEFAULT_DATE);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConfirm(""); setResult(null);
  }, [open]);

  useEffect(() => {
    if (!open || !before || result) return;
    setPreview(null);
    api.get("/admin/cleanup-before/preview", { params: { before } }).then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, before, result]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-before", { confirm, before });
      setResult(data);
      toast.success(`Opgeschoond tot ${fmtDate(before)}: ${data.accounts_deleted} accounts verwijderd.`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opschonen mislukt."); }
    setBusy(false);
  };

  const row = (label, value, testid) => (
    <div className="flex justify-between gap-3 py-1 border-b border-border/60 text-sm" data-testid={testid}>
      <span>{label}</span><b className="font-mono-num">{value}</b>
    </div>
  );
  const sysRows = (d) => (
    <>
      {row("Helpchat-sessies", d.help_sessions)}
      {row("Uitnodigingscodes (niet gebruikt door blijvende accounts)", d.invite_codes)}
      {row("Niet-betaalde bestellingen", d.test_orders)}
      {row("Logboek geplande taken", d.cron_runs)}
      {row("Monte Carlo-taken", d.mc_jobs)}
      {row("Inlogpogingen / reset-tokens", `${d.login_attempts} / ${d.password_reset_tokens}`)}
      {row("Oude testtransacties", d.payment_transactions)}
      {row("Wees-records van eerder verwijderde clubs", d.orphans)}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none heavy-shadow max-h-[90vh] overflow-y-auto" data-testid="cleanup-before-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight inline-flex items-center gap-2"><CalendarX2 className="w-5 h-5" /> Opschonen tot datum</DialogTitle>
          <DialogDescription>
            Verwijdert alle niet-beheerdersaccounts die vóór de gekozen datum zijn geregistreerd — inclusief hun clubs, scenario's, historie, budgetten en concept-facturen — plus systeemdata van vóór die datum. Beheerders en hun clubs blijven staan; accounts met een betaalde factuur worden overgeslagen (bewaarplicht). Dit kan niet ongedaan worden gemaakt.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-1" data-testid="cleanup-before-result">
            {row("Accounts verwijderd (incl. clubs en scenario's)", result.accounts_deleted, "cleanup-before-result-accounts")}
            {row("Mollie-abonnementen opgezegd", result.subscriptions_canceled)}
            {sysRows(result)}
            {result.accounts_skipped?.length > 0 && (
              <div className="mt-2 text-xs border-2 border-accent bg-accent/20 p-2" data-testid="cleanup-before-result-skipped">
                <b>Overgeslagen (betaalde facturen):</b> {result.accounts_skipped.join(", ")}
              </div>
            )}
            <div className="flex justify-end pt-3">
              <button onClick={onClose} data-testid="cleanup-before-close" className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Sluiten</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="stat-label">Alles vóór (00:00 Nederlandse tijd)</span>
              <input type="date" value={before} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setBefore(e.target.value)} data-testid="cleanup-before-date"
                className="w-full border-2 border-foreground bg-background px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </label>
            {!preview ? (
              <div className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Tellen…</div>
            ) : (
              <>
                <div data-testid="cleanup-before-preview">
                  {row("Accounts (met clubs, scenario's, teamleden)", preview.accounts, "cleanup-before-preview-accounts")}
                  {row("Clubs van die accounts", preview.clubs)}
                  {sysRows(preview)}
                </div>
                {preview.accounts_paid_skipped.length > 0 && (
                  <div className="text-xs border-2 border-accent bg-accent/20 p-2 flex gap-2" data-testid="cleanup-before-preview-skipped">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span><b>Blijven staan</b> (betaalde facturen): {preview.accounts_paid_skipped.join(", ")}.</span>
                  </div>
                )}
              </>
            )}
            <div>
              <label className="stat-label">Typ ter bevestiging: {PHRASE}</label>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder={PHRASE} data-testid="cleanup-before-confirm-input"
                className="w-full border-2 border-foreground bg-background px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-destructive" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={busy} data-testid="cleanup-before-cancel" className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Annuleren</button>
              <button onClick={run} disabled={busy || !preview || confirm.trim() !== PHRASE} data-testid="cleanup-before-run"
                className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarX2 className="w-3.5 h-3.5" />} Alles vóór {before ? fmtDate(before) : "…"} verwijderen
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
